import React from "react";
import { Field, FieldArray, Form, Formik, Field as FormikField } from "formik";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { SelectListItems } from "Components/Form/SelectListItems";
import { ProductsResultsProps } from "Components/Form/SelectListItems/interface";
import Select from "Components/Form/Select";
import Input from "Components/Form/Input";
import { listClients, ProductRulesFormValues } from "Requests/ProductsRules";
import { useQuery } from "react-query";
import { productRulesSchema } from "./schema";
import { RuleCard, FormWrapper } from "./styles";
import { SelectProductRules } from "./ProductRulesList/Select";

const PRODUCTS_CATEGORIES = {
  categories: [1, 2, 3, 4, 5, 6, 7, 8],
  subcategories: [],
};

export interface ProductRulesFormProps {
  onSubmit: (values: ProductRulesFormValues) => void;
  initialValues?: ProductRulesFormValues;
}

const defaultInitialValues = {
  product: { id: 0, product: "" },
  synonyms: "",
  rules: [{ client_id: 0, substitutions: [] }],
};

export const ProductRulesForm = ({
  onSubmit,
  initialValues = defaultInitialValues,
}: ProductRulesFormProps) => {
  const { data } = useQuery({
    queryFn: () => listClients(),
    queryKey: ["clients-product-rules"],
    refetchOnWindowFocus: false,
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={productRulesSchema}
    >
      {({ values, setFieldValue, errors, touched, handleChange }) => {
        return (
          <Form id="product-rules-form">
            <FormWrapper>
              <FormikField
                name="synonyms"
                id="synonyms"
                component={Input}
                value={values.synonyms}
                multiline
                label="Sinônimo *"
                placeholder="Digite os sinônimos separados por vírgula"
                cssClass="w-100"
                onChange={handleChange}
                error={touched.synonyms && errors.synonyms}
              />

              <div className="field-holder">
                <SelectProductRules
                  id="product"
                  label="Produto selecionado"
                  value={values.product}
                  onChange={(product) => setFieldValue("product", product)}
                  disabled={initialValues.product.id !== 0}
                  error={
                    touched.product && errors.product
                      ? String(errors.product)
                      : ""
                  }
                  placeholder="Digite para buscar o produto..."
                />
              </div>

              <h2>Adicionar regras de produto</h2>

              <FieldArray name="rules">
                {({ push, remove }) => {
                  const lastRule = values.rules[values.rules.length - 1];
                  const lastRuleComplete =
                    lastRule &&
                    lastRule.client_id != null &&
                    (lastRule.substitutions?.length ?? 0) >= 1;
                  const canAddRule = lastRuleComplete;

                  return (
                    <>
                      {values.rules.map((rule, i) => {
                        const clientIdsSelectedInOtherRules: number[] =
                          values.rules
                            .map((r, j) => (j !== i ? r.client_id : null))
                            .filter((id) => id != null && id !== 0)
                            .map((id) => Number(id));

                        const currentClientId = Number(
                          values.rules?.[i]?.client_id
                        );

                        const clientOptions = (data?.data || []).filter(
                          (opt: { id: number }) =>
                            opt.id === currentClientId ||
                            !clientIdsSelectedInOtherRules.includes(opt.id)
                        );

                        return (
                          <RuleCard key={i}>
                            <Box
                              display="flex"
                              justify="space-between"
                              align="center"
                              mb="16"
                            >
                              <strong>Regra {i + 1}</strong>
                              {values.rules.length > 1 && (
                                <Button
                                  type="button"
                                  textButton
                                  onClick={() => remove(i)}
                                  minHeight="initial"
                                >
                                  Remover regra
                                </Button>
                              )}
                            </Box>

                            <div className="field-holder">
                              <Select
                                id={`rules.${i}.client_id`}
                                readOnly={
                                  !data ||
                                  (initialValues.rules?.[i] &&
                                    initialValues.rules?.[i]?.client_id !== 0)
                                }
                                label="Operação *"
                                value={values.rules?.[i]?.client_id ?? null}
                                error={
                                  touched.rules?.[i]?.client_id &&
                                  (errors.rules?.[i] as { client_id?: string })
                                    ?.client_id
                                    ? (
                                        errors?.rules?.[i] as {
                                          client_id?: string;
                                        }
                                      )?.client_id
                                    : ""
                                }
                                options={clientOptions}
                                optionName="name"
                                placeholder="Selecione a operação"
                                onChange={(e: {
                                  target?: { value?: number };
                                  name?: string;
                                }) => {
                                  const rawValue = e?.target?.value;

                                  setFieldValue(
                                    `rules.${i}.client_id`,
                                    rawValue
                                  );
                                }}
                              />
                            </div>

                            <div className="field-holder">
                              <Field
                                name={`rules.${i}.substitutions`}
                                component={SelectListItems}
                                id={`rules.${i}.substitutions`}
                                label="Desmembramento *"
                                placeholder='Digite para buscar e aperte "Enter" para adicionar'
                                cssClass="w-100"
                                productsCategories={PRODUCTS_CATEGORIES}
                                mapValues={
                                  values.rules?.[i]?.substitutions ?? []
                                }
                                error={
                                  touched.rules?.[i]?.substitutions &&
                                  (
                                    errors.rules?.[i] as {
                                      substitutions?: string;
                                    }
                                  )?.substitutions
                                    ? (
                                        errors?.rules?.[i] as {
                                          substitutions?: string;
                                        }
                                      ).substitutions
                                    : ""
                                }
                                addToPackage={(
                                  value: ProductsResultsProps | null
                                ) => {
                                  if (!value) return;
                                  const next = [
                                    ...(values.rules?.[i]?.substitutions ?? []),
                                    value,
                                  ];
                                  setFieldValue(
                                    `rules.${i}.substitutions`,
                                    next
                                  );
                                }}
                                deleteItem={(item: ProductsResultsProps) => {
                                  const arr = (
                                    values.rules?.[i]?.substitutions ?? []
                                  ).filter((s) => s.id !== item.id);
                                  setFieldValue(
                                    `rules.${i}.substitutions`,
                                    arr
                                  );
                                }}
                              />
                            </div>
                          </RuleCard>
                        );
                      })}

                      <Button
                        type="button"
                        outlined
                        borderRadius="111"
                        onClick={() =>
                          push({ client_id: 0, substitutions: [], slug: "" })
                        }
                        disabled={!canAddRule}
                      >
                        + Adicionar regra
                      </Button>
                    </>
                  );
                }}
              </FieldArray>
            </FormWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
